import type { WebInstrumentCalendarView } from '@/shared/types/web'

export interface KpiCounts {
  failures: number
  nonCompliantEvents: number
  serviceReports: number
  dateChanges: number
  statusChanges: number
  departmentMoved: number
}

/**
 * KPI counts for the Maintenance Logs calendar panel.
 *
 * Status/Dept (and other row-derived) counts come from the loaded calendar
 * month. The activity feed uses the report `from_date`–`to_date` filter and
 * may disagree with Status/Dept KPI totals when the calendar month is navigated
 * independently of that range. Intentional: the KPI bar summarizes the visible
 * calendar, not the feed window.
 *
 * Non-Compliant Events is the server CNM occurrence count (E34-S30) — same
 * report-NCE definition as InstrumentAlertCountService when given the calendar's
 * half-open site-local UTC window — not day-cell state. Instrument-level: ignores
 * the calendar task filter (cells respect it), so the KPI can exceed visible
 * confirmed cells.
 */
export const deriveKpiCounts = (calendar: WebInstrumentCalendarView): KpiCounts => {
  const failures = calendar.failureRow.cells.reduce((sum, cell) => sum + cell.failureCount, 0)
  const nonCompliantEvents = calendar.nonComplianceEventsCount

  const serviceReports = calendar.serviceReportRow.cells.reduce(
    (sum, cell) => sum + cell.eventCount,
    0,
  )

  // Date Changes is the log-based server count (E34-S20): single source shared with the
  // report's InstrumentAlertCountService, so a date-modified log on a non-success day is
  // counted and the two surfaces always show the same number.
  const dateChanges = calendar.dateChangesCount

  const statusChanges = calendar.statusChangesRow.cells.reduce(
    (sum, cell) => sum + cell.eventCount,
    0,
  )

  const departmentMoved = calendar.departmentMovedRow.cells.reduce(
    (sum, cell) => sum + cell.eventCount,
    0,
  )

  return {
    failures,
    nonCompliantEvents,
    serviceReports,
    dateChanges,
    statusChanges,
    departmentMoved,
  }
}
